import React from 'react';
import Icon from 'components/Utils/NewIcon';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import PackYearCalculatorModal from 'components/Modals/PackYearCalculatorModal';
import { Calculator } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/AppointmentNoteSections';
import { SectionConfig, FieldConfig } from './configs';
import './style.scss';

const CPPSectionInputView = (props: any) => {
  const config: SectionConfig = props.config;

  return (
    <div className="cpp-section-input">
      <div className="cpp-section-form-area">
        {config.showPackYearCalculator && (
          <PackYearButton name={props.name} onPackYearResult={props.onPackYearResult} />
        )}
        <FormFields {...props} config={config} />
      </div>

      {props.showConfirmationDialog && (
        <ConfirmationDialog
          title="Are you sure you want to remove?"
          message="Please click 'Confirm' to proceed with the removal."
          open={props.showConfirmationDialog}
          onConfirm={props.confirmDelete}
          onCancel={() => props.setShowConfirmationDialog(false)}
          loadingState={props.loading}
        />
      )}
    </div>
  );
};

const FormFields = (props: any) => {
  const config: SectionConfig = props.config;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    props.onSave();
  };

  const mainFields = config.fields.filter((f) => f.name !== 'notes');
  const notesField = config.fields.find((f) => f.name === 'notes');

  return (
    <form className="cpp-section-form" onSubmit={handleFormSubmit}>
      <div className="cpp-section-row">
        {mainFields.map((field) => (
          <DynamicField
            key={field.name}
            field={field}
            control={props.control}
            loading={props.loading}
            watchedName={props.name}
          />
        ))}
        {config.presets && (
          <div className="cpp-section-buttons">
            {config.presets.map((presetName: string) => (
              <span
                key={presetName}
                className="cpp-section-preset-tag"
                onClick={() => props.onCustomButtonClick(presetName)}
              >
                {presetName}
              </span>
            ))}
          </div>
        )}
      </div>
      {notesField && (
        <div className="cpp-section-notes-row">
          <DynamicField
            field={notesField}
            control={props.control}
            loading={props.loading}
            watchedName={props.name}
          />
        </div>
      )}
      <div className="cpp-section-actions">
        {props.isEditing ? (
          <>
            <DefaultButton
              name="Update"
              onClick={props.onSave}
              loading={props.loading}
              disabled={!props.isValid}
              style={{ flex: 1 }}
            />
            <DefaultButton
              name="Delete"
              className="red"
              type="button"
              onClick={props.onDeleteItem}
              loading={props.loading}
              style={{ flex: 1 }}
            />
          </>
        ) : (
          <DefaultButton
            name="Save"
            onClick={props.onSave}
            loading={props.loading}
            disabled={!props.isValid}
            style={{ width: '100%' }}
          />
        )}
      </div>
    </form>
  );
};

const DynamicField = ({
  field,
  control,
  loading,
  watchedName,
}: {
  field: FieldConfig;
  control: any;
  loading: boolean;
  watchedName?: string;
}) => {
  const dynamicProps = field.dynamicProps ? field.dynamicProps({ name: watchedName }) : {};

  switch (field.type) {
    case 'autocomplete':
      return (
        <div className="cpp-section-field" style={field.style}>
          <DefaultAutoComplete
            name={field.name}
            id={`select-${field.name}`}
            control={control}
            placeholder={field.label || 'Name'}
            style={{ width: '100%', marginTop: '0' }}
            dataSource={field.options}
            popOut={true}
          />
        </div>
      );
    case 'select':
      return (
        <div className="cpp-section-field" style={field.style}>
          <DefaultSelectField
            name={field.name}
            control={control}
            label={dynamicProps.label || field.label || ''}
            style={{ width: '100%' }}
            inputs={field.options || []}
            disabled={loading || dynamicProps.disabled}
          />
        </div>
      );
    case 'text':
    default:
      return (
        <div className="cpp-section-field" style={field.style}>
          <DefaultTextField
            name={field.name}
            control={control}
            label={dynamicProps.label || field.label || ''}
            disabled={loading || dynamicProps.disabled}
            style={{ width: '100%' }}
          />
        </div>
      );
  }
};

const PackYearButton = ({
  name,
  onPackYearResult,
}: {
  name: string;
  onPackYearResult: (value: number) => void;
}) => {
  return name === 'Smoking' ? (
    <div className="cpp-section-pack-year">
      {/*@ts-expect-error*/}
      <Calculator
        name="Pack-Year"
        type="pack-year"
        calculator={
          <PackYearCalculatorModal onSubmit={(value: number) => onPackYearResult(value)} />
        }
        buttonStyle={{ margin: 0 }}
        simpleButton={false}
      />
    </div>
  ) : null;
};

export default CPPSectionInputView;
